/**
 * Expandable text — toggles between the truncated preview and the full content rendered by
 * snippets/expandable-text.liquid. The height change is animated both ways; on collapse the page
 * also scrolls back up to the top of the text (or of the closest `data-scroll-to` ancestor) so the
 * reader is not left stranded below it. Focus moves to the button of the newly shown state so
 * keyboard and screen reader users are not left on a hidden element.
 */

const DURATION = 350;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
/* Breathing room between the sticky header and the scroll target. */
const SCROLL_GAP = 16;

const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Only `.header-section` stays pinned; `top: -1px` pins it one pixel above the viewport. */
function pinnedHeaderHeight() {
	const header = document.querySelector(".header-section");
	if (!header || getComputedStyle(header).position !== "sticky") return 0;
	return Math.max(0, header.offsetHeight - 1);
}

class ExpandableText extends HTMLElement {
	/** @type {Animation | undefined} */
	animation;

	connectedCallback() {
		if (this.bound) return;
		this.bound = true;
		this.addEventListener("click", this.onClick);
	}

	get preview() {
		return this.querySelector(".expandable-text__preview");
	}

	get full() {
		return this.querySelector(".expandable-text__full");
	}

	onClick = event => {
		const toggle = event.target instanceof Element ? event.target.closest("[data-expandable-text-toggle]") : null;
		if (!toggle || !this.contains(toggle) || !this.preview || !this.full) return;

		if (toggle.getAttribute("aria-expanded") === "true") {
			this.collapse();
		} else {
			this.expand();
		}
	};

	expand() {
		const startHeight = this.offsetHeight;
		this.show(true);
		this.full.querySelector("[data-expandable-text-toggle]")?.focus({preventScroll: true});
		this.animateHeight(startHeight, this.offsetHeight);
	}

	collapse() {
		const startHeight = this.offsetHeight;

		/* Measure the collapsed height without painting it: swap, read, swap back. */
		this.show(false);
		const endHeight = this.offsetHeight;
		this.show(true);

		this.scrollToTarget();

		/* The full text stays in place while it shrinks; the preview takes over once it has. */
		this.animateHeight(startHeight, endHeight, () => {
			this.show(false);
			this.preview.querySelector("[data-expandable-text-toggle]")?.focus({preventScroll: true});
		});
	}

	/** @param {boolean} expanded */
	show(expanded) {
		this.preview.hidden = expanded;
		this.full.hidden = !expanded;
		this.toggleAttribute("expanded", expanded);
	}

	/**
	 * @param {number} from
	 * @param {number} to
	 * @param {() => void} [onDone]
	 */
	animateHeight(from, to, onDone) {
		this.animation?.cancel();

		if (prefersReducedMotion() || from === to) {
			onDone?.();
			return;
		}

		this.style.overflow = "clip";
		const animation = this.animate([{height: `${from}px`}, {height: `${to}px`}], {
			duration: DURATION,
			easing: EASING,
		});
		this.animation = animation;

		animation.onfinish = () => {
			this.style.overflow = "";
			this.animation = undefined;
			onDone?.();
		};
		animation.oncancel = () => {
			this.style.overflow = "";
		};
	}

	scrollToTarget() {
		const selector = this.dataset.scrollTo;
		const target = (selector && this.closest(selector)) || this;
		const offset = pinnedHeaderHeight() + SCROLL_GAP;
		const {top} = target.getBoundingClientRect();

		/* Already comfortably in view: leave the page where it is. */
		if (top >= offset) return;

		window.scrollTo({
			top: window.scrollY + top - offset,
			behavior: prefersReducedMotion() ? "auto" : "smooth",
		});
	}
}

if (!customElements.get("expandable-text")) {
	customElements.define("expandable-text", ExpandableText);
}
